import moment from "moment";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Box from "veneer/Box";
import Button from "veneer/Button";
import { JollyRancherChip } from "veneer/Chip";
import DescriptionList, { DescriptionListItem } from "veneer/DescriptionList";
import Link from "veneer/Link";
import styled from "veneer/styled";
import { Colors } from "veneer/Theme";
import Typography from "veneer/Typography";

import { ENV, getTraffickingUrl } from "cortex/ui/src/constant/app";
import { Asset } from "cortex/ui/src/ui/asset";
import placeholderDynamicCreative from "cortex/ui/static/image/placeholder_dynamic_creative.png";
import placeholderEmptySlot from "cortex/ui/static/image/placeholder_empty_slot.png";

export interface UpcomingSlot {
  asset_url?: string;
  creative_id?: string;
  length_in_seconds: number;
  metadata?: {
    advertiser_id?: string;
    advertiser_name?: string;
    line_item_id?: string;
    line_item_name?: string;
    order_id?: string;
    order_name?: string;
  };
  start_time: string;
  status: string;
  tracking_url?: string;
  type: string;
}

interface UpcomingSlotsTimelineProps {
  environment?: string;
  onToolbarActionChange?: (action: ReactNode | null) => void;
  slots: UpcomingSlot[];
}

const CARD_WIDTH = 300;

export const AssetFrame = styled(Box)(() => ({
  maxHeight: CARD_WIDTH,
  overflow: "hidden",
  width: "100%",

  "& img, & video": {
    maxHeight: CARD_WIDTH,
    maxWidth: "none",
    objectFit: "contain",
    width: "100%",
  },

  "& > div": {
    minHeight: CARD_WIDTH / 2,
    width: "100%",
  },
}));

export const TimelineScroller = styled(Box)(({ theme }) => ({
  overflowX: "auto",
  padding: theme.spacing(1, 0),
}));

export const TimelineTrack = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  minWidth: "max-content",
  position: "relative",

  "&::before": {
    background: Colors.gray300,
    content: '""',
    height: 4,
    left: 0,
    position: "absolute",
    right: 0,
    top: 10,
    zIndex: 0,
  },
}));

export const TimelineItem = styled(Box)(({ theme }) => ({
  minWidth: CARD_WIDTH,
  paddingTop: theme.spacing(5),
  position: "relative",
  zIndex: 1,
}));

export const TimelineChip = styled(Box)(() => ({
  display: "flex",
  justifyContent: "space-between",
  left: 0,
  position: "absolute",
  top: 0,
  width: "100%",
  zIndex: 2,
}));

const NowPlayingBadge = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

export const SlotCard = styled(Box)(({ theme }) => ({
  alignItems: "stretch",
  background: Colors.white,
  border: `1px solid ${Colors.gray200}`,
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  width: CARD_WIDTH,
}));

function getPlaceholderUrl(assetUrl?: string): string | undefined {
  if (!assetUrl) {
    return placeholderEmptySlot;
  }

  try {
    const pathname = new URL(assetUrl).pathname.toLowerCase();

    if (pathname.endsWith(".zip")) {
      return placeholderDynamicCreative;
    }
  } catch {
    if (assetUrl.toLowerCase().endsWith(".zip")) {
      return placeholderDynamicCreative;
    }
  }

  return undefined;
}

function getStatusChipColor(status: string): "yellow" | "green" | "gray" {
  if (status.startsWith("Downloading")) {
    return "yellow";
  }

  if (status === "Cached") {
    return "green";
  }

  return "gray";
}

export default function UpcomingSlotsTimeline({
  environment,
  onToolbarActionChange,
  slots,
}: UpcomingSlotsTimelineProps) {
  const [now, setNow] = useState(() => Date.now());
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const previousNowPlayingIndexRef = useRef<number | null>(null);
  const isMouseDownRef = useRef(false);
  const previousScrollLeftRef = useRef(0);
  const traffickingUrl = getTraffickingUrl(environment ?? ENV.DEPLOY_TARGET);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => window.clearInterval(interval);
  }, []);

  const nowPlayingIndex = useMemo(() => {
    return slots.findIndex((slot, index) => {
      const startTime = Number(slot.start_time) * 1000;
      if (!Number.isFinite(startTime)) {
        return false;
      }

      const nextStartTime =
        index < slots.length - 1
          ? Number(slots[index + 1].start_time) * 1000
          : NaN;

      const endTime = Number.isFinite(nextStartTime)
        ? nextStartTime
        : startTime + slot.length_in_seconds * 1000;
      return Number.isFinite(endTime) && now >= startTime && now < endTime;
    });
  }, [slots, now]);

  useEffect(() => {
    const scroller = scrollerRef.current;

    if (!scroller) {
      return;
    }

    previousScrollLeftRef.current = scroller.scrollLeft;

    const interruptAutoScroll = () => {
      setAutoScrollEnabled(false);
    };

    const handleWheel = (event: WheelEvent) => {
      const isHorizontalScroll =
        Math.abs(event.deltaX) > Math.abs(event.deltaY);

      if (isHorizontalScroll) {
        interruptAutoScroll();
      }
    };

    const handleMouseDown = () => {
      isMouseDownRef.current = true;
    };

    const handleMouseUp = () => {
      isMouseDownRef.current = false;
    };

    const handleScroll = () => {
      const scrollLeftChanged =
        scroller.scrollLeft !== previousScrollLeftRef.current;

      previousScrollLeftRef.current = scroller.scrollLeft;

      if (scrollLeftChanged && isMouseDownRef.current) {
        interruptAutoScroll();
      }
    };

    const handleTouchStart = () => {
      interruptAutoScroll();
    };

    scroller.addEventListener("wheel", handleWheel, {
      passive: true,
    });

    scroller.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mouseup", handleMouseUp);

    scroller.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    scroller.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });

    return () => {
      scroller.removeEventListener("wheel", handleWheel);
      scroller.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mouseup", handleMouseUp);
      scroller.removeEventListener("scroll", handleScroll);
      scroller.removeEventListener("touchstart", handleTouchStart);
    };
  }, []);

  useEffect(() => {
    if (!autoScrollEnabled) {
      return;
    }

    if (nowPlayingIndex < 0) {
      previousNowPlayingIndexRef.current = null;
      return;
    }

    if (previousNowPlayingIndexRef.current === nowPlayingIndex) {
      return;
    }

    previousNowPlayingIndexRef.current = nowPlayingIndex;

    const scroller = scrollerRef.current;
    const item = itemRefs.current[nowPlayingIndex];

    if (!scroller || !item) {
      return;
    }

    scroller.scrollTo({
      left: item.offsetLeft - 16,
      behavior: "smooth",
    });
  }, [autoScrollEnabled, nowPlayingIndex]);

  const resumeAutoScroll = useCallback(() => {
    setAutoScrollEnabled(true);

    const scroller = scrollerRef.current;
    const item = itemRefs.current[nowPlayingIndex];

    if (!scroller || !item) {
      return;
    }

    scroller.scrollTo({
      left: item.offsetLeft - 16,
      behavior: "smooth",
    });
  }, [nowPlayingIndex]);

  useEffect(() => {
    if (!onToolbarActionChange) {
      return;
    }

    if (!autoScrollEnabled && nowPlayingIndex >= 0) {
      onToolbarActionChange(
        <Button
          dataTestId="resume-upcoming-slots-auto-scroll-button"
          onClick={resumeAutoScroll}
          size="small"
          variant="outlined"
        >
          Resume Auto Scroll
        </Button>,
      );

      return () => {
        onToolbarActionChange(null);
      };
    }

    onToolbarActionChange(null);

    return () => {
      onToolbarActionChange(null);
    };
  }, [
    autoScrollEnabled,
    nowPlayingIndex,
    onToolbarActionChange,
    resumeAutoScroll,
  ]);

  if (!slots.length) {
    return <Typography>No scheduled slots.</Typography>;
  }

  return (
    <>
      <TimelineScroller ref={scrollerRef}>
        <TimelineTrack>
          {slots.map((slot, index) => {
            const assetUrl = slot.asset_url;
            const isNowPlaying = index === nowPlayingIndex;
            const lineItem = slot.metadata?.line_item_name ?? "";
            const order = slot.metadata?.order_name ?? "";
            const placeholderUrl = getPlaceholderUrl(assetUrl);
            const startTime = Number(slot.start_time) * 1000;
            const scheduledDateTime = Number.isFinite(startTime)
              ? moment(startTime).format("MMM Do, YYYY hh:mm:ss a")
              : "";
            const status = slot.status ?? "Not Cached";
            const statusChipColor = getStatusChipColor(status);

            return (
              <TimelineItem
                key={`${scheduledDateTime}-${lineItem}-${index}`}
                ref={(element: HTMLDivElement | null) => {
                  itemRefs.current[index] = element;
                }}
              >
                <TimelineChip>
                  <JollyRancherChip color="blue" label={scheduledDateTime} />
                  {isNowPlaying && (
                    <NowPlayingBadge>
                      <JollyRancherChip color="green" label="Now Playing" />
                    </NowPlayingBadge>
                  )}
                </TimelineChip>
                <SlotCard>
                  <AssetFrame>
                    <Asset
                      placeholder={placeholderUrl}
                      preload="none"
                      url={assetUrl}
                    />
                  </AssetFrame>
                  <DescriptionList>
                    {slot.type && (
                      <DescriptionListItem label="Type">
                        <JollyRancherChip
                          color={
                            slot.type === "advertisement" ? "orange" : "green"
                          }
                          label={
                            slot.type === "advertisement"
                              ? "Ad"
                              : "Programmatic"
                          }
                        />
                      </DescriptionListItem>
                    )}
                    <DescriptionListItem
                      dataTestId={{
                        component: `upcoming-slot-status-${index}`,
                        content: `upcoming-slot-status-${index}-content`,
                      }}
                      label="Status"
                    >
                      <JollyRancherChip
                        color={statusChipColor}
                        dataTestId={`upcoming-slot-status-chip-${index}`}
                        label={status}
                      />
                    </DescriptionListItem>
                    {order && (
                      <DescriptionListItem label="Order">
                        <Link
                          newTab
                          path={`${traffickingUrl}/#/insertion_order/active/${slot.metadata?.order_id}`}
                          title={order}
                        >
                          {order}
                        </Link>
                      </DescriptionListItem>
                    )}
                    {lineItem && (
                      <DescriptionListItem label="Line Item">
                        <Link
                          newTab
                          path={`${traffickingUrl}/#/insertion_order/active/${slot.metadata?.order_id}/campaign/${slot.metadata?.line_item_id}`}
                          title={lineItem}
                        >
                          {lineItem}
                        </Link>
                      </DescriptionListItem>
                    )}
                  </DescriptionList>
                </SlotCard>
              </TimelineItem>
            );
          })}
        </TimelineTrack>
      </TimelineScroller>
    </>
  );
}
